/**
 * Server-rendered JSON-LD.
 *
 * Server component by design - structured data has to be in the HTML the crawler receives, so
 * it must never move behind a "use client" boundary or a useEffect.
 *
 * Replaces the react-helmet-async approach the ported components used; there is no
 * HelmetProvider in this app, so those never rendered anything.
 */

// Built from code points rather than written as literals: U+2028/29 are invisible in an editor
// and get silently normalised by some tooling, so spelling them out keeps this file pure ASCII.
const BACKSLASH = String.fromCharCode(92);
const UNSAFE_IN_SCRIPT = new RegExp(
  "[<" + String.fromCharCode(0x2028) + String.fromCharCode(0x2029) + "]",
  "g",
);

/**
 * Escape the sequences that can break out of a <script> element:
 *  - `<`        guards against a `</script>` appearing inside a string value
 *  - U+2028/29  are valid in JSON but are raw line terminators in JS source
 *
 * This matters because node payloads carry DB-authored content (post titles, FAQ answers)
 * that we do not control. Output stays valid JSON and parses back to the input.
 */
function serialize(data: unknown): string {
  return JSON.stringify(data).replace(
    UNSAFE_IN_SCRIPT,
    (c) => BACKSLASH + "u" + c.charCodeAt(0).toString(16).padStart(4, "0"),
  );
}

export function JsonLd({ data }: { data: unknown }) {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serialize(data) }}
    />
  );
}
